<template>
  <div class="box-canvas" ref="stage" :style="stageStyle">
    <!--
      Zoom mode uses the same vue-inner-image-zoom widget as the other tagging
      pages (e.g. Group Tagging): click to zoom in, drag to pan. It can't be
      always-on here because a plain click must still SELECT a box, so it rides
      behind the toolbar's zoom mode. The overlay + chips hide in zoom mode.
    -->
    <div v-if="isZoom" class="box-canvas__zoom">
      <inner-image-zoom
        :src="photo.image_url"
        :key="photo.id"
        moveType="drag"
      />
    </div>
    <img
      v-else
      :src="photo.image_url"
      class="box-canvas__image"
      draggable="false"
      @load="onImageLoad"
      ref="image"
    />

    <!--
      The SVG shares the image's box exactly and uses a viewBox in NORMALIZED
      units (0..1), so every rect is positioned in the same space the boxes are
      stored in. No pixel math, and resizing the window costs nothing — the
      browser rescales. preserveAspectRatio must match the image's object-fit
      so the two never drift apart.
    -->
    <svg
      class="box-canvas__overlay"
      v-show="!isZoom"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      @pointerdown="onBackgroundPointerDown"
    >
      <rect
        v-for="box in visibleBoxes"
        :key="box.id"
        :x="boxGeom(box).x"
        :y="boxGeom(box).y"
        :width="boxGeom(box).width"
        :height="boxGeom(box).height"
        :class="rectClass(box)"
        class="bbox"
        vector-effect="non-scaling-stroke"
        @pointerdown.stop="onBoxPointerDown($event, box)"
      />

      <rect
        v-if="marquee"
        :x="marquee.x"
        :y="marquee.y"
        :width="marquee.width"
        :height="marquee.height"
        class="bbox-marquee"
        vector-effect="non-scaling-stroke"
      />

      <rect
        v-for="h in editHandles"
        :key="'handle-' + h.corner"
        :x="h.x"
        :y="h.y"
        :width="h.size"
        :height="h.size"
        class="bbox-handle"
        vector-effect="non-scaling-stroke"
        @pointerdown.stop="onHandleDown($event, h.corner)"
      />
    </svg>

    <!--
      Tag-count chips live outside the SVG so they stay a fixed pixel size
      regardless of image dimensions — inside the normalized viewBox they would
      scale with the image and become unreadable.
    -->
    <div
      v-for="box in taggedVisibleBoxes"
      :key="'chip-' + box.id"
      v-show="!isZoom"
      class="box-canvas__chip"
      :style="chipStyle(box)"
      :title="chipTitle(box)"
    >{{ chipLabel(box) }}</div>
  </div>
</template>

<script>
import InnerImageZoom from 'vue-inner-image-zoom'
import 'vue-inner-image-zoom/lib/vue-inner-image-zoom.css'

export default {
  name: 'BoxCanvas',

  components: {'inner-image-zoom': InnerImageZoom},

  props: {
    photo: {type: Object, required: true},
    boxes: {type: Array, default: () => []},
    selectedIds: {type: Array, default: () => []},
    hideTagged: {type: Boolean, default: false},
    hideInvalid: {type: Boolean, default: false},
    // Fraction of viewport height the stage may occupy. Group view packs
    // several canvases side by side and wants a smaller cap.
    maxHeightVh: {type: Number, default: 0.72},
    // Map of tagId -> name, so a chip can say WHICH tag rather than how many.
    tagNames: {type: Object, default: () => ({})},
    // Size from the container's height instead of its width. Portrait shelf
    // photos are much taller than wide, so width-driven sizing leaves a band of
    // dead space under every photo in the group grid.
    fitHeight: {type: Boolean, default: false},
    // Tag ids of the active category, or null for "all categories". When set,
    // "tagged" (blue) means "has a tag in THIS category", so switching category
    // re-reveals boxes still missing it. [IT-719]
    categoryTagIds: {type: Array, default: null},
    // Rejection reasons split invalid vs duplicate for styling + show/hide: a box
    // is a duplicate when it carries this tag id (and is status invalid). [IT-720]
    duplicateTagId: {type: Number, default: null},
    hideDuplicate: {type: Boolean, default: false},
    // Interaction mode from the toolbar. In 'zoom' the overlay hides and the
    // image pans/zooms; selection is disabled. [IT-721]
    mode: {type: String, default: 'select'},
  },

  data() {
    return {
      marquee: null,         // {x, y, width, height} in normalized units
      marqueeOrigin: null,
      marqueeAdditive: false,
      marqueeSubtractive: false,
      // Fallback when the server didn't send dimensions — read off the <img>
      // once it loads so the stage can still hold the right aspect ratio.
      loadedAspect: null,
      // Measured stage size in px. Computed rather than left to CSS: see
      // stageStyle().
      stageW: 0,
      stageH: 0,
      resizeObserver: null,
      // Draw / resize (IT-722).
      marqueeDraw: false,
      resize: null,
      resizeGeom: null,
    }
  },

  computed: {
    aspectRatio() {
      if (this.photo.image_width && this.photo.image_height) {
        return this.photo.image_width / this.photo.image_height
      }
      return this.loadedAspect || 1
    },

    /**
     * Explicit pixel size rather than `width:100%` + `aspect-ratio` + a
     * max-height.
     *
     * Those three together silently break: for a portrait photo the implied
     * height blows past the cap, the cap clamps the height, the width stays at
     * 100%, and the box ends up a different shape than the image — which
     * `object-fit: fill` then renders as a horizontally stretched shelf.
     *
     * Fitting the ratio inside both constraints ourselves keeps the box exactly
     * the image's shape, which is also what lets the SVG overlay line up: the
     * overlay assumes the stage IS the image.
     */
    stageStyle() {
      if (!this.stageW || !this.stageH) return {visibility: 'hidden'}
      return {width: `${this.stageW}px`, height: `${this.stageH}px`}
    },

    visibleBoxes() {
      return this.boxes.filter(box => {
        if (box.status === 'deleted') return false
        const dup = this.isDuplicate(box)
        if (this.hideDuplicate && dup) return false
        if (this.hideInvalid && box.status === 'invalid' && !dup) return false
        if (this.hideTagged && this.isTaggedInView(box)) return false
        return true
      })
    },

    taggedVisibleBoxes() {
      return this.visibleBoxes.filter(box => this.isTaggedInView(box) && box.status !== 'invalid')
    },

    isZoom() {
      return this.mode === 'zoom'
    },

    isDraw() {
      return this.mode === 'draw'
    },

    /** The single selected box on THIS canvas — the one that shows resize handles. */
    editBox() {
      if (!this.isDraw || this.selectedIds.length !== 1) return null
      return this.boxes.find(b => b.id === this.selectedIds[0]) || null
    },

    /** Corner handles for the box being edited (empty when none). */
    editHandles() {
      if (!this.editBox) return []
      const b = this.boxGeom(this.editBox)
      const s = 0.025
      return [
        {corner: 'nw', x: b.x - s / 2, y: b.y - s / 2, size: s},
        {corner: 'ne', x: b.x + b.width - s / 2, y: b.y - s / 2, size: s},
        {corner: 'sw', x: b.x - s / 2, y: b.y + b.height - s / 2, size: s},
        {corner: 'se', x: b.x + b.width - s / 2, y: b.y + b.height - s / 2, size: s},
      ]
    },
  },

  methods: {
    /** Tagged as the active-category view sees it — see the categoryTagIds prop. */
    isTaggedInView(box) {
      if (this.categoryTagIds == null) return !!box.tagged
      return (box.tag_ids || []).some(id => this.categoryTagIds.includes(id))
    },

    /** A rejected box carrying the Duplicate reason tag (vs a plain invalid). */
    isDuplicate(box) {
      return box.status === 'invalid' && this.duplicateTagId != null &&
        (box.tag_ids || []).includes(this.duplicateTagId)
    },

    /** Geometry to render for a box — the live resize override while dragging a handle. */
    boxGeom(box) {
      if (this.resize && this.resizeGeom && this.editBox && box.id === this.editBox.id) {
        return this.resizeGeom
      }
      return box
    },

    /** Fit the image's aspect ratio inside the available width and max height. */
    measure() {
      const stage = this.$refs.stage
      if (!stage || !stage.parentElement) return
      const availableW = stage.parentElement.clientWidth
      const maxH = window.innerHeight * this.maxHeightVh

      if (this.fitHeight) {
        // Fill the height we are given and let width follow the aspect ratio.
        const host = this.$refs.stage.parentElement
        const availableH = Math.min(host ? host.clientHeight : maxH, maxH)
        if (!availableH) return
        this.stageH = Math.floor(availableH)
        this.stageW = Math.floor(availableH * this.aspectRatio)
        return
      }

      if (!availableW) return
      const width = Math.min(availableW, maxH * this.aspectRatio)
      this.stageW = Math.floor(width)
      this.stageH = Math.floor(width / this.aspectRatio)
    },

    onImageLoad(event) {
      const img = event.target
      if (img.naturalWidth && img.naturalHeight) {
        this.loadedAspect = img.naturalWidth / img.naturalHeight
        this.$nextTick(this.measure)
      }
    },

    /**
     * Box colour is a 2x2 of (tagged?) x (selected?), per IT-642:
     *   untagged + unselected -> yellow
     *   untagged + selected   -> orange
     *   tagged   + unselected -> blue
     *   tagged   + selected   -> purple
     * Both axes stay legible at once, so "selected" never hides whether the
     * box is already done. Invalid is its own state and outranks the matrix.
     */
    rectClass(box) {
      const selected = this.selectedIds.includes(box.id)
      if (box.status === 'invalid') {
        const kind = this.isDuplicate(box) ? 'state-duplicate' : 'state-invalid'
        return {[kind]: true, 'is-selected': selected}
      }
      if (this.isTaggedInView(box)) return selected ? {'state-tagged-selected': true} : {'state-tagged': true}
      return selected ? {'state-untagged-selected': true} : {'state-untagged': true}
    },

    /**
     * Chips previously showed a tag COUNT, so every single-tag box read "1"
     * and a tagger could not tell what they had applied — which defeats the
     * point of an on-canvas indicator. Show the tag itself instead: an
     * abbreviated name, tinted by a stable colour per tag so a run of the same
     * brand reads as one block at a glance.
     */
    chipLabel(box) {
      const all = box.tag_ids || []
      const ids = this.categoryTagIds == null ? all : all.filter(id => this.categoryTagIds.includes(id))
      if (!ids.length) return ''
      const first = this.tagNames[ids[0]] || `#${ids[0]}`
      const short = first.length > 11 ? first.slice(0, 10) + '…' : first
      return ids.length > 1 ? `${short} +${ids.length - 1}` : short
    },

    chipTitle(box) {
      return (box.tag_ids || []).map(id => this.tagNames[id] || `#${id}`).join(', ')
    },

    /** Stable hue per tag id — same tag is the same colour on every photo. */
    chipColor(box) {
      const ids = box.tag_ids || []
      if (!ids.length) return 'var(--bbox-tagged)'
      const hue = (Number(ids[0]) * 47) % 360
      return `hsl(${hue}, 62%, 38%)`
    },

    chipStyle(box) {
      // Anchor to the box's top-left, nudged inside the stroke.
      return {
        left: `${box.x * 100}%`,
        top: `${box.y * 100}%`,
        background: this.chipColor(box),
        maxWidth: `${Math.max(box.width * 100, 8)}%`,
      }
    },

    /** Pointer position as a fraction of the stage, clamped to it. */
    normalizedPoint(event) {
      const rect = this.$refs.stage.getBoundingClientRect()
      if (!rect.width || !rect.height) return {x: 0, y: 0}
      return {
        x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
      }
    },

    onBoxPointerDown(event, box) {
      if (event.button !== 0) return
      // Shift+click no longer range-selects (removed IT-718); shift is reserved
      // for marquee-add on the background, so a shift-click just selects the box.
      if (event.metaKey || event.ctrlKey) this.$emit('toggle-select', box)
      else this.$emit('replace-select', box)
    },

    onBackgroundPointerDown(event) {
      if (event.button !== 0) return
      if (this.isZoom) return  // zoom mode pans instead of selecting

      this.marqueeDraw = this.isDraw  // in draw mode the drag creates a box
      this.marqueeOrigin = this.normalizedPoint(event)
      this.marqueeAdditive = event.shiftKey
      this.marqueeSubtractive = event.altKey
      this.marquee = {...this.marqueeOrigin, width: 0, height: 0}

      // Listen on window, not the SVG: the pointer routinely leaves the image
      // mid-drag and we still want the marquee to track it.
      window.addEventListener('pointermove', this.onMarqueeMove)
      window.addEventListener('pointerup', this.onMarqueeUp, {once: true})
    },

    onMarqueeMove(event) {
      if (!this.marqueeOrigin) return
      const current = this.normalizedPoint(event)
      this.marquee = {
        x: Math.min(this.marqueeOrigin.x, current.x),
        y: Math.min(this.marqueeOrigin.y, current.y),
        width: Math.abs(current.x - this.marqueeOrigin.x),
        height: Math.abs(current.y - this.marqueeOrigin.y),
      }
    },

    onMarqueeUp() {
      window.removeEventListener('pointermove', this.onMarqueeMove)

      const marquee = this.marquee
      this.marquee = null
      this.marqueeOrigin = null
      if (!marquee) return

      if (this.marqueeDraw) {
        this.marqueeDraw = false
        if (marquee.width >= 0.01 && marquee.height >= 0.01) {
          this.$emit('draw-box', {
            photoId: this.photo.id,
            x: marquee.x, y: marquee.y, width: marquee.width, height: marquee.height,
          })
        }
        return
      }

      // A click with no drag clears the selection rather than selecting nothing
      // — matches every file manager, and it is what the user means.
      const isClick = marquee.width < 0.005 && marquee.height < 0.005
      if (isClick) {
        if (!this.marqueeAdditive && !this.marqueeSubtractive) this.$emit('clear-select')
        return
      }

      // Hit-test by box CENTRE, not intersection. On a packed shelf, boxes
      // touch — intersection would drag in every neighbour the marquee grazed,
      // and the user would spend longer deselecting than selecting.
      const hits = this.visibleBoxes.filter(box => {
        const cx = box.x + box.width / 2
        const cy = box.y + box.height / 2
        return cx >= marquee.x && cx <= marquee.x + marquee.width &&
               cy >= marquee.y && cy <= marquee.y + marquee.height
      }).map(box => box.id)

      if (this.marqueeSubtractive) this.$emit('marquee-subtract', hits)
      else if (this.marqueeAdditive) this.$emit('marquee-add', hits)
      else this.$emit('marquee-replace', hits)
    },

    // Zoom + pan live in the vue-inner-image-zoom widget rendered in zoom mode
    // (see template), so there is no bespoke wheel/transform handling here. [follow-up to IT-721]

    // --- resize handles (IT-722) -----------------------------------------
    onHandleDown(event, corner) {
      if (!this.editBox) return
      this.resize = {corner, start: {x: this.editBox.x, y: this.editBox.y, width: this.editBox.width, height: this.editBox.height}}
      this.resizeGeom = {...this.resize.start}
      window.addEventListener('pointermove', this.onResizeMove)
      window.addEventListener('pointerup', this.onResizeUp, {once: true})
    },

    onResizeMove(event) {
      if (!this.resize) return
      this.resizeGeom = this.computeResize(this.resize.start, this.resize.corner, this.normalizedPoint(event))
    },

    onResizeUp() {
      window.removeEventListener('pointermove', this.onResizeMove)
      if (this.resize && this.resizeGeom && this.editBox) {
        this.$emit('resize-box', {id: this.editBox.id, ...this.resizeGeom})
      }
      this.resize = null
      this.resizeGeom = null
    },

    /** New normalized geometry from dragging `corner` to `cur`; the opposite corner anchors. */
    computeResize(start, corner, cur) {
      let x1 = start.x, y1 = start.y, x2 = start.x + start.width, y2 = start.y + start.height
      if (corner === 'nw') { x1 = cur.x; y1 = cur.y }
      else if (corner === 'ne') { x2 = cur.x; y1 = cur.y }
      else if (corner === 'sw') { x1 = cur.x; y2 = cur.y }
      else if (corner === 'se') { x2 = cur.x; y2 = cur.y }
      const clamp01 = v => Math.min(1, Math.max(0, v))
      const MIN = 0.01
      const width = Math.min(1, Math.max(MIN, Math.abs(x2 - x1)))
      const height = Math.min(1, Math.max(MIN, Math.abs(y2 - y1)))
      // Floor the position AFTER the size floor so the box always fits —
      // x + width <= 1. The server rejects anything past the edge, which
      // otherwise silently fails the save. [IT-722]
      const x = Math.min(clamp01(Math.min(x1, x2)), 1 - width)
      const y = Math.min(clamp01(Math.min(y1, y2)), 1 - height)
      return {x, y, width, height}
    },
  },

  mounted() {
    this.measure()
    // Observe the parent, not the stage: observing an element whose size we
    // set from the callback would loop.
    if (window.ResizeObserver && this.$refs.stage && this.$refs.stage.parentElement) {
      this.resizeObserver = new ResizeObserver(() => this.measure())
      this.resizeObserver.observe(this.$refs.stage.parentElement)
    }
    window.addEventListener('resize', this.measure)
  },

  watch: {
    aspectRatio: 'measure',
    // Leaving zoom mode remounts the <img> (v-else); re-fit the stage once it's back.
    mode() {
      this.$nextTick(this.measure)
    },
  },

  beforeDestroy() {
    window.removeEventListener('pointermove', this.onMarqueeMove)
    window.removeEventListener('pointermove', this.onResizeMove)
    window.removeEventListener('pointerup', this.onMarqueeUp)
    window.removeEventListener('pointerup', this.onResizeUp)
    window.removeEventListener('resize', this.measure)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
}
</script>

<style scoped>
/*
 * Box state colours live here as one block so the visual language can be
 * retuned in one place.
 */
.box-canvas {
  /* State palette per IT-642. Kept together so the visual language can be
     retuned in one place without hunting through rules. */
  --bbox-untagged: #fdd835;           /* yellow  — untagged, unselected */
  --bbox-untagged-selected: #fb8c00;  /* orange  — untagged, selected   */
  --bbox-tagged: #1e88e5;             /* blue    — tagged,   unselected */
  --bbox-tagged-selected: #8e24aa;    /* purple  — tagged,   selected   */
  --bbox-invalid: #e53935;            /* red     — rejected detection   */
  --bbox-marquee: #26c6da;

  position: relative;
  /* Size comes from stageStyle() — see the comment there. Setting width or
     max-height here would reintroduce the aspect-ratio conflict. */
  margin: 0 auto;
  user-select: none;
  background: #111;
  overflow: hidden;
}

.box-canvas__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
}

.box-canvas__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
}

.bbox {
  fill: transparent;
  stroke-width: 2;
  cursor: pointer;
  transition: stroke-width 0.08s ease;
}

.bbox.state-untagged {
  stroke: var(--bbox-untagged);
  stroke-dasharray: 5 4;
}

.bbox.state-untagged-selected {
  stroke: var(--bbox-untagged-selected);
  stroke-width: 3;
  fill: rgba(251, 140, 0, 0.18);
}

.bbox.state-tagged {
  stroke: var(--bbox-tagged);
}

.bbox.state-tagged-selected {
  stroke: var(--bbox-tagged-selected);
  stroke-width: 3;
  fill: rgba(142, 36, 170, 0.18);
}

.bbox.state-invalid {
  stroke: var(--bbox-invalid);
  stroke-dasharray: 3 3;
  opacity: 0.45;
}

.bbox.state-invalid.is-selected {
  stroke-width: 3;
  opacity: 0.8;
}

.bbox:hover {
  stroke-width: 3;
}

.bbox-marquee {
  fill: rgba(30, 136, 229, 0.12);
  stroke: var(--bbox-marquee);
  stroke-width: 1;
  stroke-dasharray: 4 3;
  pointer-events: none;
}

.box-canvas__chip {
  position: absolute;
  transform: translate(3px, 3px);
  background: var(--bbox-tagged);
  color: #fff;
  font-size: 10px;
  line-height: 1;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 2px;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.box-canvas { --bbox-duplicate: #ff7043; }

.bbox.state-duplicate {
  stroke: var(--bbox-duplicate);
  stroke-dasharray: 2 2;
  opacity: 0.6;
}

.bbox.state-duplicate.is-selected {
  stroke-width: 3;
  opacity: 0.9;
}
.bbox-handle {
  fill: #fff;
  stroke: var(--bbox-tagged-selected);
  stroke-width: 2;
  cursor: nwse-resize;
}

/* Zoom-mode widget fills the measured stage (which already matches the image's
   aspect ratio, so object-fit never distorts). */
.box-canvas__zoom {
  position: absolute;
  inset: 0;
}
</style>

<style>
/* Unscoped: scoped styles can't reach vue-inner-image-zoom's internal nodes.
   Mirrors the inner-image-zoom block in GroupTagging.vue. */
.box-canvas__zoom .iiz,
.box-canvas__zoom .iiz__img {
  width: 100%;
  height: 100%;
  display: block;
}

.box-canvas__zoom .iiz__img {
  object-fit: contain;
}
</style>
